import { useEffect, useRef, useState } from "react";
import { Link } from "@/components/site/LocaleLink";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, LifeBuoy } from "lucide-react";
import { IMG } from "@/data/images";
import { Picture } from "@/components/site/Picture";
import { useLang } from "@/context/LanguageContext";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { maskLine } from "@/hooks/useReveal";

// Backdrop slides, in order. The first is the LCP image and loads eagerly; the
// rest stay lazy, so adding a slide costs nothing on first paint. Each `alt` is
// empty on purpose — the slides are decorative behind the <h1>, and announcing
// three rotating descriptions would just be noise to a screen reader.
const SLIDES = [IMG.hero, IMG.flockScale, IMG.healthyFlock];

const SLIDE_MS = 6000;

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.6]);

  // Scroll-linked values are driven by style, so MotionConfig can't switch them
  // off — opt out of the parallax explicitly.
  const parallax = reduced ? {} : { y, scale };

  // Reduced-motion visitors get slide 1, held still — no rotation, no crossfade.
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    if (reduced || SLIDES.length < 2) return;
    const id = setInterval(() => setSlide((i) => (i + 1) % SLIDES.length), SLIDE_MS);
    return () => clearInterval(id);
  }, [reduced]);

  const lines = [t.hero.l1, t.hero.l2, t.hero.l3];

  return (
    <section
      ref={ref}
      className="relative flex min-h-[92svh] items-end overflow-hidden pb-16 pt-24"
      data-testid="hero"
    >
      <motion.div style={parallax} className="absolute inset-0 -z-10">
        {/* Slide 1 stays mounted underneath as the base layer, so there is never
            a frame of empty background between crossfades. */}
        <Picture
          img={SLIDES[0]}
          alt="Modern commercial poultry house"
          className="absolute inset-0 h-full w-full object-cover"
          sizes="100vw"
          priority
        />
        <AnimatePresence>
          {slide > 0 && (
            <motion.div
              key={slide}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
              className="absolute inset-0"
            >
              <Picture
                img={SLIDES[slide]}
                alt=""
                className="h-full w-full object-cover"
                sizes="100vw"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 -z-10 bg-gradient-to-t from-forest-deep via-forest/70 to-forest/25"
      />
      <div className="scrim-start absolute inset-0 -z-10" />
      <div className="noise pointer-events-none absolute inset-0 -z-10 opacity-[0.05]" />

      <div className="container-x w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="eyebrow mb-6 text-gold-light"
        >
          {t.hero.kicker}
        </motion.p>

        <h1 className="max-w-4xl font-display text-4xl font-extrabold leading-[1.15] tracking-tight text-cream sm:text-6xl lg:text-7xl">
          {lines.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span
                className="block pb-[0.15em]"
                variants={maskLine}
                custom={i}
                initial="hidden"
                animate="show"
              >
                {i === 2 ? <span className="text-gold-light">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-cream/85 sm:text-lg"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <Link
            to="/products"
            data-testid="hero-cta-products"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-cream px-7 py-4 text-sm font-semibold text-forest-deep transition-all duration-300 hover:-translate-y-1 hover:bg-white"
          >
            {t.hero.cta1}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl-flip" />
          </Link>
          <Link
            to="/support"
            data-testid="hero-cta-support"
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-cream/40 px-7 py-4 text-sm font-semibold text-cream transition-all duration-300 hover:-translate-y-1 hover:border-cream hover:bg-cream/10"
          >
            <LifeBuoy className="h-4 w-4" />
            {t.hero.cta2}
          </Link>
        </motion.div>

        {!reduced && SLIDES.length > 1 && (
          <div className="mt-12 flex gap-2.5" data-testid="hero-slide-nav">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setSlide(i)}
                aria-label={`${t.hero.slideLabel} ${i + 1}`}
                aria-current={i === slide}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i === slide ? "w-10 bg-gold-light" : "w-4 bg-cream/40 hover:bg-cream/70"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
